import { VegaChartConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { DefaultError, useQuery } from '@tanstack/react-query';

import {
  fetchFooterData,
  FetchFooterDataQueryKey,
} from '../../api/portalGateway/footerData';
import {
  type PageFilterSelections,
  useCombinedPageFilterSelections,
} from '../../store/hooks/useCombinedPageFilterSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { prependRoleId } from '../../utils/prependRoleId';

type ChartGroup = {
  [key: string]: string | number | boolean | null;
};

type FooterChartDataResult = {
  transformedData: ChartGroup[] | undefined;
  isLoading: boolean;
  error: DefaultError | null;
};

export const useFooterChartData = (
  widgetId: string,
  pageFilterInstanceId: string,
  tableFilterInstanceId: string,
  enabled: boolean,
  isGroupDataConfigured: boolean,
  entityConfig: VegaChartConfigDto,
  stakeAdjusted?: boolean,
  selectionOverride?: {
    pageFilter: PageFilterSelections;
    tableFilter: PageFilterSelections;
  }
): FooterChartDataResult => {
  const { groupedColumnForChart, tableFooterTitle } = entityConfig;
  const combinedFilterSelections =
    useCombinedPageFilterSelections(pageFilterInstanceId);
  const storedTableFilterSelections = useSelections(tableFilterInstanceId);
  const pageFilterSelections =
    selectionOverride?.pageFilter ?? combinedFilterSelections;
  const tableFilterSelections =
    selectionOverride?.tableFilter ?? storedTableFilterSelections;

  const {
    data: footerData,
    isLoading,
    error,
  } = useQuery({
    queryKey: prependRoleId<FetchFooterDataQueryKey>([
      'footerDataForVega',
      widgetId,
      pageFilterSelections,
      tableFilterSelections,
      'ALL',
      groupedColumnForChart
        ? [
            {
              colId: groupedColumnForChart.id,
              level: 0,
            },
          ]
        : undefined,
      stakeAdjusted,
    ]),
    queryFn: fetchFooterData,
    enabled,
  });

  /**
   * Function to transform footer table data to vega chart data
   * Footer data:
   * {
   *    mainFooter: { value_x: 6 },
   *    groupFooter: { value_x: { total: 6, grouped: { EUR: 1, GBP: 1, USD: 1 } } }
   * }
   *
   * Chart data:
   *  { footerTitle: 'footer_title', value_x: 6},
   */
  const transformFooterDataToChartData = () => {
    if (footerData?.data.mainFooter) {
      const chartFooterData: ChartGroup[] = [
        {
          footerTitle: tableFooterTitle || 'Total',
        },
      ];
      for (const key of Object.keys(footerData.data.mainFooter)) {
        chartFooterData[0][key] = footerData.data.mainFooter[key];
      }

      return chartFooterData;
    }
  };

  /**
   * Function to transform group table data to vega chart data
   * Footer data:
   * {
   *    mainFooter: { value_x: 6 },
   *    groupFooter: {
   *      value_x: {
   *        total: 1,
   *        grouped: {
   *        EUR: {
   *          total: 1,
   *        },
   *        GBP: {
   *          total: 1,
   *        },
   *        USD: {
   *          total: 1,
   *        },
   *        }
   *      },
   *      value_y: {
   *        total: 1.1,
   *        grouped: {
   *          EUR: {
   *            total: 1.1,
   *          },
   *        }
   *      },
   *    }
   * }
   *
   * Chart data:
   * [
   *  { entity: 'EUR', value_x: 1, value_y: 1.1},
   *  { entity: 'GBP', value_x: 1},
   *  { entity: 'USD', value_x: 1}
   * ]
   * It uses the `groupedColumnForChart` as key for the group
   */
  const transformGroupDataToChartData = () => {
    const chartGroups: ChartGroup[] = [];
    const groupFooter = footerData?.data.groupFooter;

    if (groupFooter && groupedColumnForChart) {
      for (const colName of Object.keys(groupFooter)) {
        if (groupFooter[colName] && groupFooter[colName].grouped) {
          for (const groupName of Object.keys(groupFooter[colName].grouped)) {
            const chartGroupIndex = chartGroups.findIndex(
              (g) =>
                g[groupedColumnForChart.key as keyof ChartGroup] === groupName
            );

            // Find the total value from the nested group data keys
            const chartMetricValue =
              groupFooter[colName].grouped?.[groupName].total;
            if (chartGroupIndex !== -1) {
              chartGroups[chartGroupIndex][colName] = chartMetricValue ?? null;
            } else {
              chartGroups.push({
                [groupedColumnForChart.key]: groupName,
                [colName]: chartMetricValue ?? null,
              });
            }
          }
        }
      }
    }
    return chartGroups;
  };

  const transformedData = isGroupDataConfigured
    ? transformGroupDataToChartData()
    : transformFooterDataToChartData();

  return {
    transformedData,
    isLoading,
    error,
  };
};
